import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * A chat row's title as a field while the user renames it. `start` is
 * undefined where the row can't be renamed: a chat still waiting for its
 * id, or no rename to call. `done` takes the field's result: a new title is
 * renamed, and a key that ended it (Enter or Escape) gives focus back to
 * the row through `focusRow` once it is drawn again.
 */
export function useRenameInPlace({
  id,
  rename,
  focusRow,
}: {
  id: string | null;
  rename?: (id: string, title: string) => void;
  focusRow: () => void;
}): {
  editing: boolean;
  start: (() => void) | undefined;
  done: (next: string | null, byKey: boolean) => void;
} {
  const [editing, setEditing] = useState(false);
  const refocus = useRef(false);
  const focusRowRef = useRef(focusRow);
  focusRowRef.current = focusRow;
  useEffect(() => {
    if (editing || !refocus.current) return;
    refocus.current = false;
    focusRowRef.current();
  }, [editing]);

  const start = useMemo(
    () => (id !== null && rename ? () => setEditing(true) : undefined),
    [id, rename]
  );
  const done = useCallback(
    (next: string | null, byKey: boolean) => {
      refocus.current = byKey;
      setEditing(false);
      if (next !== null && id !== null) rename?.(id, next);
    },
    [id, rename]
  );
  return { editing: editing && start !== undefined, start, done };
}
